/**
 * UploadedDocumentsList - Viser opplastede dokumenter gruppert etter sak
 * Smart case-gruppering basert på AI-klassifisering av dokumentinnhold
 */
import { useState, useEffect } from 'react';
import { FileText, ChevronDown, ChevronUp, Trash2, Eye, Loader2, Upload, FolderOpen, Tag } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const FILE_TYPE_COLORS = {
  '.pdf': 'text-red-400 bg-red-400/10',
  '.docx': 'text-blue-400 bg-blue-400/10',
  '.jpg': 'text-emerald-400 bg-emerald-400/10',
  '.jpeg': 'text-emerald-400 bg-emerald-400/10',
  '.png': 'text-emerald-400 bg-emerald-400/10',
};

const DOC_TYPE_LABELS = {
  'kontrakt': 'Kontrakt',
  'vedtak': 'Vedtak',
  'klage': 'Klage',
  'kvittering': 'Kvittering',
  'brev': 'Brev',
  'vognkort': 'Vognkort',
  'rapport': 'Rapport',
  'annet': 'Dokument',
};

export function UploadedDocumentsList({ moduleId, onUploadClick }) {
  const { user, token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [caseGroups, setCaseGroups] = useState([]);
  const [ungrouped, setUngrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [expandedCase, setExpandedCase] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const fetchDocuments = async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(
        `${API}/api/module-documents/list/${moduleId}?user_id=${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
        setCaseGroups(data.case_groups || []);
        setUngrouped(data.ungrouped || []);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDocuments();
  }, [moduleId, user?.id]);

  // Refresh list when new documents are uploaded
  useEffect(() => {
    const handler = () => fetchDocuments();
    window.addEventListener('document-uploaded', handler);
    return () => window.removeEventListener('document-uploaded', handler);
  }, [moduleId, user?.id]);

  const viewFullAnalysis = async (docId) => {
    setViewLoading(true);
    try {
      const res = await fetch(
        `${API}/api/module-documents/${docId}?user_id=${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setViewingDoc(data.document);
      }
    } catch (err) {
      toast.error('Kunne ikke laste analyse');
    }
    setViewLoading(false);
  };

  const deleteDocument = async (docId) => {
    if (!confirm('Er du sikker pa at du vil slette dette dokumentet?')) return;
    try {
      const res = await fetch(
        `${API}/api/module-documents/${docId}?user_id=${user.id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setDocuments(prev => prev.filter(d => d.id !== docId));
        if (viewingDoc?.id === docId) setViewingDoc(null);
        toast.success('Dokument slettet');
        fetchDocuments(); // Re-fetch to update groups
      }
    } catch (err) {
      toast.error('Kunne ikke slette dokument');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-[var(--copper)] animate-spin" />
      </div>
    );
  }

  const totalDocs = documents.length;

  return (
    <div className="space-y-4" data-testid="uploaded-documents-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[var(--text-main)]">
          Dokumentbibliotek {totalDocs > 0 && <span className="text-sm font-normal text-[var(--text-muted)]">({totalDocs})</span>}
        </h3>
        {onUploadClick && (
          <button
            onClick={onUploadClick}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-[var(--copper)]/10 text-[var(--copper)] border border-[var(--copper)]/30 rounded-lg hover:bg-[var(--copper)]/20 transition-colors"
            data-testid="upload-new-btn"
          >
            <Upload className="w-3.5 h-3.5" />
            Last opp nytt
          </button>
        )}
      </div>

      {totalDocs === 0 ? (
        <div className="text-center py-8 bg-[var(--bg-surface)] rounded-xl border border-[var(--border-subtle)]">
          <FileText className="w-10 h-10 text-[var(--text-muted)] mx-auto mb-3 opacity-40" />
          <p className="text-sm text-[var(--text-muted)]">Ingen opplastede dokumenter enna</p>
          <p className="text-xs text-[var(--text-muted)] mt-1 opacity-70">
            Bruk "Last opp dokument"-knappen for a analysere kontrakter og brev
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Case groups */}
          {caseGroups.map((group, idx) => (
            <CaseGroup
              key={idx}
              group={group}
              isExpanded={expandedCase === idx}
              onToggle={() => setExpandedCase(expandedCase === idx ? null : idx)}
              expandedDoc={expandedDoc}
              setExpandedDoc={setExpandedDoc}
              viewingDoc={viewingDoc}
              viewLoading={viewLoading}
              onViewAnalysis={viewFullAnalysis}
              onDelete={deleteDocument}
            />
          ))}

          {/* Ungrouped documents */}
          {ungrouped.length > 0 && (
            <div className="space-y-2">
              {caseGroups.length > 0 && (
                <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mt-4">
                  Uklassifiserte dokumenter
                </p>
              )}
              {ungrouped.map(doc => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  isExpanded={expandedDoc === doc.id}
                  onToggle={() => {
                    if (expandedDoc === doc.id) {
                      setExpandedDoc(null);
                      setViewingDoc(null);
                    } else {
                      setExpandedDoc(doc.id);
                      viewFullAnalysis(doc.id);
                    }
                  }}
                  viewingDoc={viewingDoc}
                  viewLoading={viewLoading}
                  onDelete={() => deleteDocument(doc.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function CaseGroup({ group, isExpanded, onToggle, expandedDoc, setExpandedDoc, viewingDoc, viewLoading, onViewAnalysis, onDelete }) {
  const docCount = group.documents?.length || 0;
  const docType = DOC_TYPE_LABELS[group.document_type] || 'Dokument';

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl overflow-hidden" data-testid={`case-group-${group.case_subject}`}>
      {/* Case header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 hover:bg-[var(--bg-highlight)] transition-colors"
      >
        <div className="p-2 rounded-lg bg-[var(--copper)]/10">
          <FolderOpen className="w-4 h-4 text-[var(--copper)]" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-[var(--text-main)]">{group.case_subject}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--copper)]/10 text-[var(--copper)]">
              {docType}
            </span>
            <span className="text-xs text-[var(--text-muted)]">
              {docCount} {docCount === 1 ? 'dokument' : 'dokumenter'}
            </span>
            {group.parties?.length > 0 && (
              <span className="text-xs text-[var(--text-muted)]">
                {group.parties.join(' / ')}
              </span>
            )}
          </div>
        </div>
        {isExpanded ? <ChevronUp className="w-4 h-4 text-[var(--text-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" />}
      </button>

      {/* Expanded documents */}
      {isExpanded && (
        <div className="border-t border-[var(--border-subtle)] p-3 space-y-2">
          {group.documents?.map(doc => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              isExpanded={expandedDoc === doc.id}
              onToggle={() => {
                if (expandedDoc === doc.id) {
                  setExpandedDoc(null);
                } else {
                  setExpandedDoc(doc.id);
                  onViewAnalysis(doc.id);
                }
              }}
              viewingDoc={viewingDoc}
              viewLoading={viewLoading}
              onDelete={() => onDelete(doc.id)}
              compact
            />
          ))}
        </div>
      )}
    </div>
  );
}


function DocumentRow({ doc, isExpanded, onToggle, viewingDoc, viewLoading, onDelete, compact }) {
  const typeColor = FILE_TYPE_COLORS[doc.file_type] || 'text-[#8899A8] bg-[#2C3E50]';
  const docTypeLabel = DOC_TYPE_LABELS[doc.classification?.document_type] || '';

  return (
    <div className={`${compact ? '' : 'bg-[var(--bg-surface)] border border-[var(--border-subtle)]'} rounded-lg overflow-hidden`} data-testid={`doc-${doc.id}`}>
      <div className="flex items-center gap-3 p-3">
        <div className={`p-2 rounded-lg ${typeColor}`}>
          <FileText className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--text-main)] truncate">{doc.filename}</p>
          <div className="flex items-center gap-2">
            <p className="text-xs text-[var(--text-muted)]">
              {new Date(doc.created_at).toLocaleDateString('nb-NO', {
                day: 'numeric', month: 'short', year: 'numeric'
              })}
              {doc.file_size && ` - ${(doc.file_size / 1024).toFixed(0)} KB`}
            </p>
            {docTypeLabel && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-highlight)] text-[var(--text-muted)] border border-[var(--border-subtle)]">
                {docTypeLabel}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onToggle}
            className="p-1.5 rounded hover:bg-[var(--copper)]/20 text-[var(--text-muted)] hover:text-[var(--copper)]"
            title="Vis analyse"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded hover:bg-red-500/20 text-[var(--text-muted)] hover:text-red-400"
            title="Slett"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expanded analysis */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-[var(--border-subtle)]">
          {viewLoading ? (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className="w-4 h-4 text-[var(--copper)] animate-spin" />
              <span className="text-sm text-[var(--text-muted)]">Laster analyse...</span>
            </div>
          ) : viewingDoc ? (
            <div
              className="mt-3 prose prose-sm prose-invert max-w-none text-[var(--text-muted)] leading-relaxed
                [&_h2]:text-[var(--copper)] [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2
                [&_h3]:text-[var(--text-main)] [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mt-3 [&_h3]:mb-1
                [&_strong]:text-[var(--text-main)] [&_ul]:space-y-1 [&_li]:text-sm
                [&_p]:text-sm [&_p]:mb-2"
              dangerouslySetInnerHTML={{ __html: formatMd(viewingDoc.analysis) }}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}


function formatMd(text) {
  if (!text) return '';
  return text
    .replace(/## (.*?)$/gm, '<h2>$1</h2>')
    .replace(/### (.*?)$/gm, '<h3>$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/^- (.*?)$/gm, '<li>$1</li>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>');
}


export default UploadedDocumentsList;
